import { ComponentType, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workspace } from '@robocorp/robocloud-internal-cds-sdk';

import { useWorkspaceByShortNameQuery } from '~/queries';
import { useActions, useParams, useSelector } from '~/hooks';
import ViewLoader from '~/components/ViewLoader';

export const withWorkspace =
  (WrappedComponent: ComponentType<{ currentWorkspace: Workspace }>) => () => {
    const navigate = useNavigate();
    const { organizationShortName, workspaceShortName } = useParams<
      'organizationShortName' | 'workspaceShortName'
    >();
    const reduxCurrentWorkspaceId = useSelector((state) => state.ui.currentWorkspaceId);

    const { setCurrentWorkspaceId } = useActions(({ uiActions }) => ({
      setCurrentWorkspaceId: uiActions.setCurrentWorkspaceId,
    }));

    const { data: currentWorkspace, isLoading: isLoadingWorkspace } = useWorkspaceByShortNameQuery(
      { organizationShortName, workspaceShortName },
      { refetchOnWindowFocus: false },
    );

    const currentWorkspaceId = currentWorkspace?.id;

    const handleError = useCallback(() => {
      window.localStorage.removeItem('currentWorkspaceShortName');
      window.sessionStorage.removeItem('currentWorkspaceId');
      setCurrentWorkspaceId('');

      return navigate('/workspace-not-found');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSuccess = useCallback((id: string, shortName: string) => {
      window.localStorage.setItem('currentWorkspaceShortName', shortName);
      window.sessionStorage.setItem('currentWorkspaceId', id);
      setCurrentWorkspaceId(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isLoadingWorkspace) {
        return;
      }

      if (!currentWorkspaceId) {
        handleError();
        return;
      }

      handleSuccess(currentWorkspaceId, workspaceShortName);
    }, [handleError, handleSuccess, isLoadingWorkspace, currentWorkspaceId, workspaceShortName]);

    const isCurrentWorkspaceIdSynced =
      !!currentWorkspaceId && currentWorkspaceId === reduxCurrentWorkspaceId;

    if (isLoadingWorkspace || !isCurrentWorkspaceIdSynced) {
      return <ViewLoader />;
    }
    return <WrappedComponent currentWorkspace={currentWorkspace} />;
  };
